import { FC, useEffect } from 'react'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { Theme } from 'Components'
import {
  ThemeContextProvider,
  CampaignContextProvider,
  SSEContextProvider,
  useAppContext,
  NotificationContextProvider,
} from 'Contexts'
import { AuthorizedRoutes } from './AuthorizedRoutes'
import { LoginPage } from './Pages'
import './App.scss'
import './Assets/FontAwesomePro6/css/all.css'
import { User } from 'oidc-client-ts'

const App: FC = () => {
  const auth = useAuth()
  const { setRebuild } = useAppContext()
  const isLoggedIn = auth && auth.isAuthenticated

  useEffect(() => {
    const handleUserSignedIn = () => {
      if (auth.user && auth.user.access_token && auth.user.refresh_token) {
        localStorage.setItem('eltoro_token', auth.user.access_token)
        localStorage.setItem('eltoro_refresh_token', auth.user.refresh_token)
      }
    }

    const handleAccessTokenExpiring = () => {
      const refreshToken = localStorage.getItem('eltoro_refresh_token')
      if (refreshToken) {
        try {
          auth.startSilentRenew()
        } catch (error) {
          console.error('Failed to refresh token', error)
          auth.removeUser()
        }
      }
    }

    const handleUserLoaded = (user: User) => {
      if (user && user.access_token) {
        localStorage.setItem('eltoro_token', user?.access_token)
        setRebuild(true)
      }
    }

    const handleAccessTokenExpired = () => {
      // Attempt a silent refresh before logging out — the token may have
      // expired while the tab was frozen/backgrounded and the refresh
      // token could still be valid.
      auth.signinSilent().catch(() => {
        auth.removeUser()
        auth.revokeTokens()
      })
    }

    auth.events.addUserSignedIn(handleUserSignedIn)
    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring)
    auth.events.addUserLoaded(handleUserLoaded)
    auth.events.addAccessTokenExpired(handleAccessTokenExpired)

    return () => {
      auth.events.removeUserSignedIn(handleUserSignedIn)
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring)
      auth.events.removeUserLoaded(handleUserLoaded)
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired)
    }
  }, [auth, setRebuild])

  // Proactively refresh token when user returns to a backgrounded tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!auth.isAuthenticated) return
      if (auth.user?.expired) {
        auth.signinSilent().catch(() => {
          auth.removeUser()
        })
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [auth])

  // Cross-tab token synchronization via localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'eltoro_token') {
        if (!e.newValue) {
          // Token removed in another tab — log out here too
          auth.removeUser()
        } else if (e.newValue !== e.oldValue) {
          // Token refreshed in another tab — rebuild SDK instances
          setRebuild(true)
        }
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [auth, setRebuild])

  if (!isLoggedIn)
    return (
      <Router>
        <ThemeContextProvider>
          <div className="App">
            <Theme />
            <LoginPage />
          </div>
        </ThemeContextProvider>
      </Router>
    )
  // the router needs to wrap the contexts so they have access to the router methods
  return (
    <Router>
      <ThemeContextProvider>
        <SSEContextProvider>
          <NotificationContextProvider>
            <CampaignContextProvider>
              <Theme />
              <div className="App">{isLoggedIn && <AuthorizedRoutes />}</div>
            </CampaignContextProvider>
          </NotificationContextProvider>
        </SSEContextProvider>
      </ThemeContextProvider>
    </Router>
  )
}

export default App
